import { events } from "@/common/constants";
import { themesMap } from "@/common/theme";
import { useToggleEventAnimation } from "@/mono/lib/hooks/useAnimation";
import { themeTransition } from "@/mono/lib/styles/globalStyles";
import { useCallback, useMemo, useRef } from "react";
import { Color } from "three";

// HINT: We use a function so we always get current ref
export function useThemeTransitionGLUniforms(
  getMesh,
  uniformsToChange = [],
  uniforms,
  convertToSRGB = true
) {
  const colorNames = useMemo(
    () => uniformsToChange.map((u) => uniforms[u].__colorName),
    [uniforms]
  );

  const currentColors = useRef(uniformsToChange.map(() => new Color()));
  const toColors = useRef(uniformsToChange.map(() => new Color()));

  const setColors = useCallback(({ data: toTheme }) => {
    toColors.current = toColors.current.map((c, i) => {
      const newColor = c.setStyle(themesMap[toTheme][colorNames[i]]);
      if (convertToSRGB) newColor.convertLinearToSRGB();

      return newColor;
    });
    currentColors.current = currentColors.current.map((c, i) => {
      return getMesh().material.uniforms[uniformsToChange[i]].value;
    });
  }, []);

  useToggleEventAnimation({
    inParams: {
      event: events.SWITCH_THEME,
      duration: themeTransition.duration / 1000,
      ease: themeTransition.easeGSAP,
      onStart: setColors,
      onUpdate: (v) => {
        currentColors.current.forEach((color, i) => {
          const uniform = uniformsToChange[i];
          const meshUniform = getMesh().material.uniforms[uniform];

          meshUniform.value.lerpColors(color, toColors.current[i], v);
          meshUniform.needsUpdate = true;
        });
      },
    },
  });
}

export function useThemeTransitionGL(getMesh, colorName, convertToSRGB = true) {
  const currentColor = useRef(new Color());
  const toColor = useRef(new Color());

  const lerpColor = useRef(new Color());

  const setColors = useCallback(({ data: toTheme }) => {
    const newColor = toColor.current.setStyle(themesMap[toTheme][colorName]);
    if (convertToSRGB) newColor.convertLinearToSRGB();
    toColor.current = newColor;

    currentColor.current = getMesh().material.color;
  }, []);

  useToggleEventAnimation({
    inParams: {
      event: events.SWITCH_THEME,
      duration: themeTransition.duration / 1000,
      ease: themeTransition.easeGSAP,
      onStart: setColors,
      onUpdate: (v) => {
        if (!getMesh()) return;
        lerpColor.current.lerpColors(currentColor.current, toColor.current, v);

        getMesh().material.color.copy(lerpColor.current);
      },
    },
  });
}
